/**
 * Project Context
 *
 * Provides global state management for projects using React Context API.
 * Follows the same pattern used in the codebase (React Hooks + localStorage).
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Project,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectContext as ProjectContextType,
} from '../types/project';
import * as projectService from '../services/projectService';

// Create context with default values
const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

// Local storage key for active project
const ACTIVE_PROJECT_KEY = 'activeProjectId';

/**
 * ProjectProvider component
 * Wraps the application to provide project state and operations
 */
export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load all projects for the user
   */
  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await projectService.getUserProjects(100, 0); // Load all projects
      if (response.success && response.data) {
        setProjects(response.data);

        // Try to restore active project from localStorage
        const savedActiveProjectId = localStorage.getItem(ACTIVE_PROJECT_KEY);
        if (savedActiveProjectId) {
          const activeProject = response.data.find(p => p.id === savedActiveProjectId);
          if (activeProject) {
            setCurrentProject(activeProject);
          } else {
            // Saved project doesn't exist anymore, clear it
            localStorage.removeItem(ACTIVE_PROJECT_KEY);
          }
        }

        // If no current project, try to get the active one from the server
        if (!savedActiveProjectId) {
          try {
            const activeResponse = await projectService.getActiveProject();
            if (activeResponse.success && activeResponse.data) {
              setCurrentProject(activeResponse.data);
              localStorage.setItem(ACTIVE_PROJECT_KEY, activeResponse.data.id);
            }
          } catch (err) {
            // No active project set, that's okay
            console.debug('No active project found');
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar projetos';
      setError(errorMessage);
      console.error('Failed to load projects:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new project
   * Automatically sets it as the active project
   */
  const createProject = useCallback(async (data: CreateProjectRequest): Promise<Project> => {
    setError(null);

    try {
      const response = await projectService.createProject(data);
      if (response.success && response.data) {
        const newProject = response.data;

        // Add to projects list
        setProjects(prev => [newProject, ...prev]);

        // Set as current project
        setCurrentProject(newProject);
        localStorage.setItem(ACTIVE_PROJECT_KEY, newProject.id);

        // Also set as active on the server
        try {
          await projectService.setActiveProject(newProject.id);
        } catch (err) {
          console.error('Failed to set active project on server:', err);
        }

        return newProject;
      }

      throw new Error('Resposta inválida do servidor');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao criar projeto';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Update an existing project
   */
  const updateProject = useCallback(
    async (projectId: string, data: UpdateProjectRequest): Promise<Project> => {
      setError(null);

      try {
        const response = await projectService.updateProject(projectId, data);
        if (response.success && response.data) {
          const updatedProject = response.data;

          // Update in projects list
          setProjects(prev => prev.map(p => (p.id === projectId ? updatedProject : p)));

          // Update current project if it's the one being updated using functional update
          setCurrentProject(prev => (prev?.id === projectId ? updatedProject : prev));

          return updatedProject;
        }

        throw new Error('Resposta inválida do servidor');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Falha ao atualizar projeto';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  /**
   * Delete a project
   */
  const deleteProject = useCallback(
    async (projectId: string): Promise<void> => {
      setError(null);

      try {
        const response = await projectService.deleteProject(projectId);
        if (response.success) {
          // Use functional update to get current projects state
          setProjects(prev => {
            const remainingProjects = prev.filter(p => p.id !== projectId);
            return remainingProjects;
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Falha ao deletar projeto';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  /**
   * Set a project as the active one, or deactivate all projects if null
   */
  const setActiveProject = useCallback(
  async (projectId: string | null): Promise<void> => {
    setError(null);

    try {
      if (projectId === null) {
        setCurrentProject(null);
        localStorage.removeItem(ACTIVE_PROJECT_KEY);
        setProjects(prev =>
          prev.map(p => ({
            ...p,
            is_active: false,
          }))
        );
        return;
      }

      const response = await projectService.setActiveProject(projectId);
      if (response.success && response.data) {
        // ✅ Atualiza TODOS os estados antes de resolver a Promise
        setCurrentProject(response.data);
        localStorage.setItem(ACTIVE_PROJECT_KEY, projectId);
        setProjects(prev =>
          prev.map(p => ({
            ...p,
            is_active: p.id === projectId,
          }))
        );
        
        // ✅ Aguarda a próxima renderização para garantir que o estado foi aplicado
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao definir projeto ativo';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  },
  []
);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load projects on mount - only once
  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // More precise route matching using regex
    const isProjectRoute = /^\/project\/[^/]+/.test(location.pathname);

    // If we're not on a project route and there's an active project, deactivate it
    if (!isProjectRoute && currentProject) {
      setCurrentProject(null);
      localStorage.removeItem(ACTIVE_PROJECT_KEY);
    }
  }, [location.pathname, currentProject]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<ProjectContextType>(
    () => ({
      currentProject,
      projects,
      isLoading,
      error,
      createProject,
      updateProject,
      deleteProject,
      setActiveProject,
      loadProjects,
      clearError,
    }),
    [currentProject, projects, isLoading, error, createProject, updateProject, deleteProject, setActiveProject, loadProjects, clearError]
  );

  return <ProjectContext.Provider value={contextValue}>{children}</ProjectContext.Provider>;
};

/**
 * Custom hook to use the ProjectContext
 * Throws an error if used outside of ProjectProvider
 */
export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);

  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }

  return context;
};
